import { useCallback } from 'react';

import { useAnalyticsEvents } from '@atlaskit/analytics-next';

import { useNotesData } from '@confluence/notes-util/entry-points/hooks/useNotesData';
import { useNotesActions } from '@confluence/notes-util/entry-points/NotesStore';
import { useContentType, usePageContentId, usePageSpaceKey } from '@confluence/page-context';
import { useCopyContentLink } from '@confluence/copy-content-link';
import { useIsEditorPage } from '@confluence/route-manager/entry-points/useIsEditorPage';
import { markErrorAsHandled } from '@confluence/graphql/entry-points/markErrorAsHandled';
import { getMonitoringClient } from '@confluence/monitoring';
import { Attribution } from '@confluence/error-boundary/entry-points/Attribution';

import { generateQuote } from '../quoteToNoteHelper';

const handleErrors = (errors: Error[]) => {
	errors.forEach((e) => {
		markErrorAsHandled(e);
		getMonitoringClient().submitError(e, {
			attribution: Attribution.PAGE_MODES,
		});
	});
};

export const useCreateNoteFromSelection = ({ source }: { source: string }) => {
	const { createAnalyticsEvent } = useAnalyticsEvents();
	const { createNote } = useNotesData();
	const { setActiveNoteId } = useNotesActions();
	const [contentId] = usePageContentId();
	const [spaceKey] = usePageSpaceKey();
	const [contentType] = useContentType();
	const isEditRoute = useIsEditorPage();
	const { makeContentLink } = useCopyContentLink({
		pageMode: isEditRoute ? 'edit' : 'view',
		contentType: contentType ?? '',
		contentId: contentId ?? '',
		spaceKey: spaceKey ?? '',
		source,
	});
	const pageUrl = makeContentLink() ?? '';

	const fireAddToNoteAnalytics = useCallback(() => {
		createAnalyticsEvent({
			type: 'sendUIEvent',
			data: {
				source,
				action: 'clicked',
				actionSubject: 'button',
				actionSubjectId: 'addToNote',
			},
		}).fire();
	}, [createAnalyticsEvent, source]);

	const handleCreateNoteClick = useCallback(async () => {
		fireAddToNoteAnalytics();

		const activeSelection = window.getSelection();
		const highlightedText = activeSelection?.toString() || '';

		const { data, errors } = await createNote({
			variables: { body: JSON.stringify(generateQuote(highlightedText, pageUrl)) },
		});

		const createNoteErrors = errors || (data?.createNote?.errors as Error[]);
		if (createNoteErrors) {
			handleErrors(createNoteErrors);
		}

		setActiveNoteId(data?.createNote?.note?.ari);
	}, [fireAddToNoteAnalytics, createNote, setActiveNoteId, pageUrl]);

	return { handleCreateNoteClick };
};
